(() => {
  if (window.CistaGalleryNavigation) return;
  const controllers = new Map();
  class GalleryNavigation {
    constructor(gallery) {
      this.gallery = gallery;
      this.viewer = gallery.querySelector('[id^="GalleryViewer"]');
      this.track = this.viewer?.querySelector('[id^="Slider-Gallery"]');
      this.rail = gallery.querySelector('[id^="GalleryThumbnails"]');
      this.dots = gallery.querySelector('[data-cista-gallery-dots]');
      this.abort = new AbortController();
      this.desktop = matchMedia('(min-width: 750px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      gallery.cistaGalleryController = this;
      const options = { signal: this.abort.signal };
      gallery.addEventListener('cista:media-change', event => this.sync(event.detail.mediaId), options);
      this.dots?.addEventListener('click', event => {
        const button = event.target.closest('[data-cista-media-target]');
        if (button) this.activate(button.dataset.cistaMediaTarget);
      }, options);
      this.dots?.addEventListener('keydown', event => this.onKeydown(event, false), options);
      this.rail?.addEventListener('keydown', event => this.onKeydown(event, true), options);
      this.track?.addEventListener('scroll', () => this.onScroll(), { ...options, passive: true });
      this.track?.addEventListener('scrollend', () => this.settle(), options);
      this.track?.addEventListener('pointerdown', event => this.onPointerDown(event), { ...options, passive: true });
      window.addEventListener('pointerup', event => this.onPointerUp(event), options);
      window.addEventListener('pointercancel', event => this.onPointerUp(event), options);
      // Native touch scrolling cancels PointerEvents before the finger lifts.
      // Keep the contact state until TouchEvents end, including a held swipe.
      this.track?.addEventListener('touchstart', event => this.onTouchStart(event), { ...options, passive: true });
      window.addEventListener('touchend', event => this.onTouchEnd(event), { ...options, passive: true });
      window.addEventListener('touchcancel', event => this.onTouchEnd(event), { ...options, passive: true });
      this.track?.addEventListener('click', event => this.onPreviewClick(event), options);
      this.desktop.addEventListener('change', () => this.alignAfterResize(), options);
      this.refresh();
      this.observer = new MutationObserver(() => this.refresh());
      if (this.track) this.observer.observe(this.track, { childList: true });
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.track?.clientWidth;
        if (width && width !== this.width) this.alignAfterResize();
      });
      if (this.track) this.resizeObserver.observe(this.track);
    }

    refresh() {
      if (!this.dots || !this.track) return;
      // Only original Shopify media own IDs, players, modal openers and dots.
      const slides = [...this.track.children].filter(slide => slide.hasAttribute('data-media-id'));
      const signature = slides.map(slide => slide.dataset.mediaId).join('|');
      if (signature === this.signature && slides.every((slide, index) => slide === this.slides[index])) return;
      this.slides = slides;
      this.signature = signature;
      this.current = slides.find(slide => slide.classList.contains('is-active'))?.dataset.mediaId || slides[0]?.dataset.mediaId;
      this.dots.replaceChildren(...slides.map((slide, index) => {
        const button = document.createElement('button');
        const thumbnail = this.rail?.querySelector('[data-target="' + slide.dataset.mediaId + '"] button');
        button.type = 'button';
        button.className = 'cista-gallery__dot';
        button.dataset.cistaMediaTarget = slide.dataset.mediaId;
        button.setAttribute('aria-label', thumbnail?.getAttribute('aria-label') || this.dots.dataset.imageLabel.replace('[index]', String(index + 1)));
        button.setAttribute('aria-controls', slide.id);
        return button;
      }));
      this.dots.hidden = slides.length < 2;
      this.sync(this.current);
      this.alignAfterResize();
    }

    preview(slide) {
      const item = document.createElement('li');
      item.className = 'product__media-item cista-gallery__sentinel';
      item.dataset.cistaLoopMedia = slide.dataset.mediaId;
      item.setAttribute('aria-hidden', 'true');
      item.setAttribute('role', 'presentation');
      const image = slide.querySelector('img');
      const container = document.createElement('div');
      container.className = 'cista-gallery__preview';
      if (image) {
        // Copy pixels only, never interactive/custom elements or their IDs.
        const copy = document.createElement('img');
        ['src', 'srcset', 'sizes', 'width', 'height'].forEach(name => {
          if (image.hasAttribute(name)) copy.setAttribute(name, image.getAttribute(name));
        });
        copy.alt = '';
        copy.loading = 'eager';
        copy.decoding = 'async';
        copy.draggable = false;
        const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
        if (Number.isFinite(ratio) && ratio > 0) container.style.aspectRatio = String(ratio);
        container.append(copy);
      }
      item.append(container);
      return item;
    }

    buildLoop() {
      this.track.querySelectorAll('[data-cista-loop-media]').forEach(slide => slide.remove());
      if (this.desktop.matches || this.slides.length < 2) return;
      // Two visual neighbours at each end keep BOTH edge slivers identical
      // before and after the invisible boundary reset, including a two-photo set.
      const count = this.slides.length;
      this.track.prepend(this.preview(this.slides[(count - 2) % count]), this.preview(this.slides[count - 1]));
      this.track.append(this.preview(this.slides[0]), this.preview(this.slides[1 % count]));
    }

    get physicalSlides() {
      return [...this.track.children].filter(slide => slide.hasAttribute('data-media-id') || slide.hasAttribute('data-cista-loop-media'));
    }

    targetLeft(slide) {
      return slide.offsetLeft + slide.offsetWidth / 2 - this.track.clientWidth / 2;
    }

    nearest() {
      return this.physicalSlides.reduce((nearest, slide) =>
        !nearest || Math.abs(this.targetLeft(slide) - this.track.scrollLeft) < Math.abs(this.targetLeft(nearest) - this.track.scrollLeft) ? slide : nearest, null);
    }

    sync(mediaId) {
      if (!mediaId) return;
      this.current = mediaId;
      this.syncDots(mediaId);
    }

    syncDots(mediaId) {
      if (!mediaId) return;
      this.dots?.querySelectorAll('[data-cista-media-target]').forEach(button => {
        if (button.dataset.cistaMediaTarget === mediaId) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }

    commit(slide, announce = true) {
      if (!slide) return;
      const changed = !slide.classList.contains('is-active');
      this.gallery.activationVersion = (this.gallery.activationVersion || 0) + 1;
      this.slides.forEach(item => item.classList.toggle('is-active', item === slide));
      if (changed) this.gallery.playActiveMedia(slide);
      const thumbnail = this.rail?.querySelector('[data-target="' + slide.dataset.mediaId + '"]');
      if (thumbnail) {
        this.gallery.setActiveThumbnail(thumbnail);
        if (changed && announce) this.gallery.announceLiveRegion(slide, this.slides.indexOf(slide) + 1);
      }
      this.sync(slide.dataset.mediaId);
    }

    activate(mediaId, instant = false) {
      const original = this.slides.find(slide => slide.dataset.mediaId === mediaId);
      if (!original) return;
      if (this.desktop.matches) {
        this.gallery.setActiveMedia(mediaId, false, { scrollPage: false });
        return;
      }
      this.gesture = null;
      this.pointerId = null;
      const targets = this.physicalSlides.filter(slide => (slide.dataset.mediaId || slide.dataset.cistaLoopMedia) === mediaId);
      const target = targets.reduce((closest, slide) => Math.abs(this.targetLeft(slide) - this.track.scrollLeft) < Math.abs(this.targetLeft(closest) - this.track.scrollLeft) ? slide : closest, original);
      this.pending = mediaId;
      this.commit(original);
      if (instant || this.motion.matches) {
        this.jump(original);
        this.pending = null;
      } else {
        this.track.scrollTo({ left: this.targetLeft(target), behavior: 'smooth' });
        this.scheduleSettle();
      }
    }

    onPointerDown(event) {
      if (this.desktop.matches || event.isPrimary === false || (event.button !== undefined && event.button !== 0)) return;
      this.pending = null;
      this.pointerId = event.pointerId;
      this.gesture = this.nearest();
      clearTimeout(this.settleTimer);
    }

    onPointerUp(event) {
      if (this.pointerId !== event.pointerId) return;
      this.pointerId = null;
      this.scheduleSettle();
    }

    onTouchStart(event) {
      if (this.desktop.matches) return;
      this.touching = event.touches.length > 0;
      this.pending = null;
      if (event.touches.length === 1 && !this.gesture) this.gesture = this.nearest();
      if (event.touches.length > 1) this.gesture = null;
      clearTimeout(this.settleTimer);
    }

    onTouchEnd(event) {
      if (!this.touching) return;
      this.touching = event.touches.length > 0;
      if (!this.touching) {
        this.pointerId = null;
        this.scheduleSettle();
      }
    }

    onScroll() {
      if (this.desktop.matches || this.changing) return;
      // Reflect the centred photo on the next paint, including while a finger
      // remains down. Playback and the committed selection still wait for snap.
      if (this.dotFrame == null) {
        this.dotFrame = requestAnimationFrame(() => {
          this.dotFrame = null;
          if (this.desktop.matches || this.changing || this.pending) return;
          const slide = this.targetSlide();
          if (slide) this.syncDots(slide.dataset.mediaId || slide.dataset.cistaLoopMedia);
        });
      }
      this.scheduleSettle();
    }

    scheduleSettle() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 160);
    }

    targetSlide() {
      const physical = this.physicalSlides;
      let slide = this.nearest();
      if (this.pending) slide = physical.find(item => item.dataset.mediaId === this.pending) || slide;
      if (!slide) return;
      if (this.gesture) {
        const start = physical.indexOf(this.gesture);
        const end = physical.indexOf(slide);
        // Both live dots and settlement use the same one-photo gesture limit,
        // even if browser momentum skips its mandatory snap-stop.
        if (start >= 0 && Math.abs(end - start) > 1) slide = physical[start + Math.sign(end - start)];
      }
      return slide;
    }

    settle() {
      if (this.desktop.matches || this.changing || this.pointerId != null || this.touching) return;
      clearTimeout(this.settleTimer);
      this.cancelDotFrame();
      const slide = this.targetSlide();
      if (!slide) return;
      this.gesture = null;
      this.pending = null;
      const mediaId = slide.dataset.mediaId || slide.dataset.cistaLoopMedia;
      const original = this.slides.find(item => item.dataset.mediaId === mediaId);
      this.commit(original);
      if (slide !== original || Math.abs(this.targetLeft(slide) - this.track.scrollLeft) > 1) this.jump(original);
    }

    cancelDotFrame() {
      cancelAnimationFrame(this.dotFrame);
      this.dotFrame = null;
    }

    jump(slide) {
      if (!slide || this.desktop.matches) return;
      this.cancelDotFrame();
      this.changing = true;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollTo({ left: this.targetLeft(slide), behavior: 'instant' });
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.track.style.removeProperty('scroll-snap-type');
        this.changing = false;
      });
    }

    onPreviewClick(event) {
      const preview = event.target.closest('[data-cista-loop-media]');
      if (!preview) return;
      const original = this.slides.find(slide => slide.dataset.mediaId === preview.dataset.cistaLoopMedia);
      if (!original) return;
      this.activate(original.dataset.mediaId, true);
      // A boundary preview delegates to the original Shopify opener, so zoom
      // and deferred media retain their existing listeners and accessible dialog.
      original.querySelector('modal-opener button, .deferred-media__poster')?.click();
    }

    onKeydown(event, vertical) {
      const keys = vertical ? ['ArrowUp', 'ArrowDown', 'Home', 'End'] : ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      const group = vertical ? this.rail : this.dots;
      const buttons = [...group.querySelectorAll(vertical ? '[data-target] button' : '[data-cista-media-target]')];
      const current = buttons.indexOf(event.target.closest('button'));
      if (current < 0) return;
      event.preventDefault();
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (event.key === keys[0] ? -1 : 1) + buttons.length) % buttons.length;
      const button = buttons[index];
      this.activate(vertical ? button.closest('[data-target]').dataset.target : button.dataset.cistaMediaTarget);
      button.focus({ preventScroll: true });
    }

    alignAfterResize() {
      if (!this.track) return;
      clearTimeout(this.settleTimer);
      this.cancelDotFrame();
      this.syncDots(this.current);
      this.gesture = null;
      this.pointerId = null;
      this.pending = null;
      this.changing = true;
      this.touching = false;
      this.width = this.track.clientWidth;
      this.buildLoop();
      const current = this.slides.find(slide => slide.dataset.mediaId === this.current);
      if (!this.desktop.matches) this.jump(current);
      else {
        cancelAnimationFrame(this.frame);
        this.track.style.removeProperty('scroll-snap-type');
        this.track.scrollTo({ left: 0, behavior: 'instant' });
        this.changing = false;
        const thumbnail = this.rail?.querySelector('[data-target="' + this.current + '"]');
        if (thumbnail) this.gallery.setActiveThumbnail(thumbnail);
      }
    }

    destroy() {
      this.abort.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.settleTimer);
      this.cancelDotFrame();
      cancelAnimationFrame(this.frame);
      this.track?.querySelectorAll('[data-cista-loop-media]').forEach(slide => slide.remove());
      this.track?.style.removeProperty('scroll-snap-type');
      delete this.gallery.cistaGalleryController;
    }
  }
  const mount = async (root = document) => {
    await customElements.whenDefined('media-gallery');
    controllers.forEach((controller, gallery) => {
      if (!gallery.isConnected) { controller.destroy(); controllers.delete(gallery); }
    });
    const galleries = [...root.querySelectorAll('media-gallery[data-cista-gallery]')];
    if (root.matches?.('media-gallery[data-cista-gallery]')) galleries.unshift(root);
    galleries.forEach(gallery => {
      if (!controllers.has(gallery) && gallery.isConnected) controllers.set(gallery, new GalleryNavigation(gallery));
    });
  };
  window.CistaGalleryNavigation = { mount };
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('product-info:loaded', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, gallery) => {
      if (event.target.contains(gallery)) { controller.destroy(); controllers.delete(gallery); }
    });
  });
  mount();
})();
